import React, { useState, useMemo } from 'react';
import { ChevronRight, Workflow } from 'lucide-react';
import logger from '../../utils/logger';
import projectService from '../../services/projectService';
import { useAuth } from '../../AuthProvider';
import SetWorkflowModal from '../modals/SetWorkflowModal';

// Import sub-components
import YoutubeLinkCard from './detail/YoutubeLinkCard';
import OverviewCard from './detail/OverviewCard';
import DescriptionCard from './detail/DescriptionCard';
import LeaveNoteCard from './detail/LeaveNoteCard';
import SynopsisCard from './detail/SynopsisCard';
import AllVersionsCard from './detail/AllVersionsCard';
import EntitiesCard from './detail/EntitiesCard';
import PromptCard from './detail/PromptCard';
import TranscriptionCard from './detail/TranscriptionCard';
import IABTaxonomyCard from './detail/IABTaxonomyCard';

const SynopsisProjectDetailPage = ({ project, details, onClose, onDelete, onRefresh }) => {
  const { user } = useAuth();
  const metadata = details?.metadata || project?.metadata;
  const synopsis = details?.synopsis || project?.synopsis;
  const transcript = details?.transcript || project?.transcript;
  
  // State for version selection
  const [selectedVersionIndex, setSelectedVersionIndex] = useState(null);
  
  const canDeleteProject = user?.role === 'admin' || user?.app_metadata?.role === 'admin';
  const canApproveReject = user?.role === 'admin' || user?.role === 'pm' || user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'pm';
  const workflowStatus = project?.workflowStatus ?? project?.workflow_status ?? details?.project?.workflow_status;
  const currentUserIsAssignee = details?.current_user_is_assignee === true;
  const canEditContent = currentUserIsAssignee || canApproveReject; // PM/Admin can always edit
  const showAcceptButton = workflowStatus === 'ready_for_review' && currentUserIsAssignee;
  const showSubmitButton = (workflowStatus === 'in_progress' || workflowStatus === 'rejected') && currentUserIsAssignee;
  const showApproveButton = workflowStatus === 'ready_for_pm_review' && canApproveReject;
  const showRejectButton = workflowStatus === 'ready_for_pm_review' && canApproveReject;
  const showDeliverButton = workflowStatus === 'pm_approved' && canApproveReject;
  const isDelivered = workflowStatus === 'delivered';
  const [workflowModalOpen, setWorkflowModalOpen] = useState(false);
  const canSetWorkflow = user?.role === 'admin' || user?.role === 'pm' || user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'pm';
  const [accepting, setAccepting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [delivering, setDelivering] = useState(false);
  const handleAccept = async () => {
    if (!project?.id || accepting) return;
    setAccepting(true);
    try {
      await projectService.updateWorkflowStatus(project.id, 'in_progress');
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Accept failed:', err);
      alert(err.response?.data?.detail || 'Failed to accept task');
    } finally {
      setAccepting(false);
    }
  };
  const handleSubmit = async () => {
    if (!project?.id || submitting) return;
    setSubmitting(true);
    try {
      await projectService.updateWorkflowStatus(project.id, 'ready_for_pm_review');
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Submit failed:', err);
      alert(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };
  const handleApprove = async () => {
    if (!project?.id || approving) return;
    setApproving(true);
    try {
      await projectService.updateWorkflowStatus(project.id, 'pm_approved');
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Approve failed:', err);
      alert(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setApproving(false);
    }
  };
  const handleReject = async () => {
    if (!project?.id || rejecting) return;
    if (!window.confirm('Reject this submission? It will go back to the reviewer.')) return;
    setRejecting(true);
    try {
      await projectService.updateWorkflowStatus(project.id, 'rejected');
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Reject failed:', err);
      alert(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setRejecting(false);
    }
  };
  const handleDeliver = async () => {
    if (!project?.id || delivering) return;
    setDelivering(true);
    try {
      await projectService.updateWorkflowStatus(project.id, 'delivered');
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Deliver failed:', err);
      alert(err.response?.data?.detail || 'Failed to deliver');
    } finally {
      setDelivering(false);
    }
  };
  
  // Get all synopsis versions
  const synopsisVersions = useMemo(() => {
    return details?.synopsis_versions || [];
  }, [details?.synopsis_versions]);
  
  // Determine current synopsis based on selected version index
  const currentSynopsis = useMemo(() => {
    logger.log(`🔍 Recalculating currentSynopsis:`, {
      selectedVersionIndex,
      synopsisVersionsLength: synopsisVersions.length,
      hasSynopsis: !!synopsis
    });
    
    // PRIORITY 1: If a version is explicitly selected, use it
    if (selectedVersionIndex !== null && synopsisVersions.length > 0) {
      if (synopsisVersions[selectedVersionIndex]) {
        return synopsisVersions[selectedVersionIndex];
      }
    }
    
    // PRIORITY 2: If synopsisVersions exist, use the latest one
    if (synopsisVersions.length > 0) {
      return synopsisVersions[synopsisVersions.length - 1];
    }
    
    // PRIORITY 3: Fallback to synopsis from details
    if (synopsis) {
      return synopsis;
    }
    
    return null;
  }, [selectedVersionIndex, synopsisVersions, synopsis]);
  
  // Handle version selection from All Versions table
  const handleVersionSelect = (index) => {
    logger.log(`🔄 Selected version ${index + 1}`);
    setSelectedVersionIndex(index);
    // Scroll to top to see the synopsis
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Handle regeneration complete
  const handleRegenerateComplete = async () => {
    setSelectedVersionIndex(null); // Reset to show latest
    if (onRefresh) {
      await onRefresh();
    }
  };
  
  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-neutral-950">
      {/* Header */}
      <div className="bg-white dark:bg-neutral-950 border-b border-gray-200 dark:border-neutral-800 shadow-sm flex-shrink-0 h-15">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left: Back button and title */}
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
                title="Back to projects"
              >
                <ChevronRight className="w-6 h-6 text-gray-600 dark:text-neutral-400 rotate-180" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-neutral-100">
                  {project?.title || project?.name || 'Synopsis Project'}
                </h1>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Workflow: {project?.workflowStatusDisplayName ?? project?.workflow_status_display_name ?? details?.project?.workflow_status_display_name ?? '—'}
                </p>
              </div>
            </div>
            
            {/* Right: actions */}
            <div className="flex items-center space-x-2">
              {showAcceptButton && (
                <button
                  type="button"
                  onClick={handleAccept}
                  disabled={accepting}
                  className="bg-primary hover:bg-primary/90 text-background px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center disabled:opacity-50"
                >
                  {accepting ? 'Accepting…' : 'Accept'}
                </button>
              )}
              {showSubmitButton && (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-primary hover:bg-primary/90 text-background px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center disabled:opacity-50"
                >
                  {submitting ? 'Submitting…' : 'Submit'}
                </button>
              )}
              {showApproveButton && (
                <button
                  type="button"
                  onClick={handleApprove}
                  disabled={approving}
                  className="bg-primary hover:bg-primary/90 text-background px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center disabled:opacity-50"
                >
                  {approving ? 'Approving…' : 'Approve'}
                </button>
              )}
              {showRejectButton && (
                <button
                  type="button"
                  onClick={handleReject}
                  disabled={rejecting}
                  className="bg-destructive text-destructive-foreground border border-border px-4 py-2 rounded-md text-sm font-medium flex items-center disabled:opacity-50"
                >
                  {rejecting ? 'Rejecting…' : 'Reject'}
                </button>
              )}
              {showDeliverButton && (
                <button
                  type="button"
                  onClick={handleDeliver}
                  disabled={delivering}
                  className="bg-primary hover:bg-primary/90 text-background px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center disabled:opacity-50"
                >
                  {delivering ? 'Sending…' : 'Deliver to Creator'}
                </button>
              )}
              {canSetWorkflow && !isDelivered && (
                <button
                  type="button"
                  onClick={() => setWorkflowModalOpen(true)}
                  className="border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 hover:bg-gray-100 dark:hover:bg-neutral-800 text-gray-700 dark:text-neutral-200 px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2"
                >
                  <Workflow className="w-4 h-4" /> Set Workflow
                </button>
              )}
              {canDeleteProject && (
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this project?')) {
                      onDelete(project.id);
                    }
                  }}
                  className="bg-gray-200 dark:bg-neutral-800 hover:bg-gray-300 dark:hover:bg-neutral-700 text-gray-700 dark:text-neutral-200 border border-2 border-gray-300 dark:border-neutral-700 px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center"
                >
                  DELETE
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <SetWorkflowModal
        isOpen={workflowModalOpen}
        onClose={() => setWorkflowModalOpen(false)}
        projectId={project?.id}
        projectType={project?.projectType ?? project?.project_type}
        isLocked={isDelivered}
        onSaved={onRefresh}
      />
      
      {/* Content - 3 Column Layout */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 pb-8">
          <div className="grid grid-cols-12 gap-4">
            {/* Left Column - Info Cards (2/12 = ~17%) */}
            <div className="col-span-3 space-y-3">
              <YoutubeLinkCard metadata={metadata} />
              <OverviewCard 
                project={project} 
                metadata={metadata} 
                transcript={transcript}
                currentSynopsis={currentSynopsis}
              />
              <DescriptionCard metadata={metadata} />
              <LeaveNoteCard projectId={project?.id} initialNote={project?.notes || details?.notes || ''} />
            </div>
            
            {/* Middle Column - Synopsis & Versions (6/12 = 50%) */}
            <div className="col-span-6 space-y-6">
              <SynopsisCard 
                currentSynopsis={currentSynopsis}
                synopsisVersions={synopsisVersions}
                user={user}
                selectedVersionIndex={selectedVersionIndex}
                onRefresh={onRefresh}
                readOnly={isDelivered || !canEditContent}
              />
              <AllVersionsCard 
                synopsisVersions={synopsisVersions}
                onVersionSelect={handleVersionSelect}
              />
              <IABTaxonomyCard iabTaxonomy={currentSynopsis?.iab_taxonomy} />
            </div>
            
            {/* Right Column - Prompt & Transcription (4/12 = ~33%) */}
            <div className="col-span-3 space-y-3">
              <PromptCard 
                currentSynopsis={currentSynopsis}
                details={details}
                projectId={project?.id}
                onRegenerateComplete={handleRegenerateComplete}
              />
              <TranscriptionCard transcript={transcript} />
              <EntitiesCard entities={currentSynopsis?.entities} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SynopsisProjectDetailPage;
